import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchMaintenanceStatus } from '../api/maintenance.api';
import {
  INACTIVE_SECTION,
  type MaintenanceSection,
  type MaintenanceSectionKey,
} from '../types/maintenance.types';

export const maintenanceQueryKey = ['maintenance', 'client-area'] as const;

/** How often the status is re-checked while the tab is visible. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Live maintenance status, polled every minute (plus on tab focus / reconnect).
 *
 * Fails OPEN: while loading, or if the endpoint is unreachable, every section
 * reports `inactive` so a broken status call can never lock users out. The
 * last successful answer is kept while a refetch is in flight.
 */
export function useMaintenance() {
  const query = useQuery({
    queryKey: maintenanceQueryKey,
    queryFn: fetchMaintenanceStatus,
    staleTime: 30_000,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const section = (key: MaintenanceSectionKey): MaintenanceSection =>
    query.data?.sections[key] ?? INACTIVE_SECTION;

  return {
    all: section('all'),
    medicalRecord: section('medical_record'),
    appointments: section('appointments'),
    section,
    isFetching: query.isFetching,
    /** True once the first status request has finished (success OR error). */
    isSettled: !query.isPending,
    refetch: query.refetch,
  };
}

/** Status of a single section (convenience for route gates). */
export function useMaintenanceSection(key: MaintenanceSectionKey): MaintenanceSection {
  return useMaintenance().section(key);
}
